/**
 * Getting out of trouble, taught once: a car stuck against something (the gas held, going nowhere) is shown how
 * to back out, and a crash that ruins the lap is shown the restart. The first WORDS_TIMES times each comes up:
 * words and a pulse; after that, only a soft glow on the control; and once a player has done it, on their own or
 * once when told (they know where it is now), never again on that device. In the driving
 * lesson's practice lap (`teaching`) they always come with words, whatever the player knows, and aren't counted.
 *
 * Restart is suggested at most once a race, and only early enough in the lap that a fresh one is the quicker way
 * on (late in a lap, carrying on to the line gives a flying start instead): after a spin (facing the wrong way),
 * a lap gone invalid, a hard hit on a wall; and, anywhere in the lap, after being stuck a long while.
 * Pure: main.ts feeds it the car each frame and the events, and shows what it says.
 */

export type RescueHint = 'backOut' | 'restart';
export type HintLevel = 'words' | 'glow';
export type RescueShow = Record<RescueHint, HintLevel | null>;

/** What a player has learned and how often they've been shown, kept on the device (main.ts's local storage). */
export interface RescueMemory {
  read(key: string): string | null;
  write(key: string, value: string): void;
}

/** The car, as much as the coach needs of it. */
export interface RescueCar {
  vx: number;
  vy: number;
  /** 1 while reversing. */
  reverse: number;
}

/** Sat still this long with the gas held: stuck. */
export const STUCK_SECONDS = 1.5;
/** Still stuck this long after that: restart is offered too. */
export const STUCK_LONG_SECONDS = 5.5;
/** Facing the wrong way this long: spun. */
const SPUN_SECONDS = 0.6;
/** A wall hit at least this hard (m/s into the wall: about 80 km/h at a real angle). */
export const HARD_HIT = 10;
/** Restart is only suggested in the first part of the lap (fraction of the lap), stuck excepted. */
export const RESTART_BEFORE = 0.7;
/** How long the restart suggestion stays up, s. */
const RESTART_SHOW = 5;
/** Shown with words this many times, then only a glow. */
export const WORDS_TIMES = 3;

const KEY = {
  knows: { backOut: 'gp.knowsBackOut', restart: 'gp.knowsRestart' },
  shown: { backOut: 'gp.hintsBackOut', restart: 'gp.hintsRestart' },
} as const;

export type RescueNote = 'backOutHint' | 'backedOut' | 'restartHint' | 'restartAfterHint' | 'knewBackOut' | 'knewRestart';

export class Rescue {
  private stuckFor = 0;
  private spunFor = 0;
  /** This race has moved off (a reverse on the grid isn't knowing how to back out of a wall). */
  private moved = false;
  private restartOffered = false;
  private restartLeft = 0;
  private wasReversing = false;
  /** The driving lesson's practice lap: always words, never counted towards a player's own learning. */
  private teaching = false;
  private show: RescueShow = { backOut: null, restart: null };

  constructor(
    private memory: RescueMemory,
    /** Something worth counting happened (stats). */
    private note: (n: RescueNote) => void = () => {},
  ) {}

  /** What's up now. */
  get showing(): RescueShow {
    return { ...this.show };
  }

  /** A race (re)starts: nothing up, nothing counted towards this race yet. */
  raceStart(): void {
    this.stuckFor = 0;
    this.spunFor = 0;
    this.moved = false;
    this.restartOffered = false;
    this.restartLeft = 0;
    this.wasReversing = false;
    this.show = { backOut: null, restart: null };
  }

  /** Taken down without any learning (the lesson, a replay, the poster). */
  clear(): void {
    this.show = { backOut: null, restart: null };
    this.restartLeft = 0;
  }

  /**
   * One frame of racing. `gas`: the throttle held; `progress`: how far round the lap (0 to 1; 1 before the line);
   * `facingBack`: the car points the wrong way along the track; `teaching`: the lesson's practice lap. Returns
   * true when what's showing changed.
   */
  frame(dt: number, car: RescueCar, gas: number, progress: number, facingBack: boolean, teaching = false): boolean {
    this.teaching = teaching;
    const before = `${this.show.backOut}|${this.show.restart}`;
    const speed = Math.hypot(car.vx, car.vy);
    if (speed > 8) this.moved = true;
    const reversing = car.reverse !== 0 && car.vx < -0.3;

    // Backing out: told, it's counted, and they know how from now on; on their own (nothing up), likewise.
    if (reversing && !this.wasReversing) {
      if (this.show.backOut) {
        if (!this.teaching) {
          this.note('backedOut');
          this.memory.write(KEY.knows.backOut, '1');
        }
      } else if (this.moved && !this.teaching && !this.knows('backOut')) {
        this.memory.write(KEY.knows.backOut, '1');
        this.note('knewBackOut');
      }
    }
    this.wasReversing = reversing;

    const stuckNow = speed < 0.6 && car.reverse === 0 && gas > 0.5;
    this.stuckFor = stuckNow ? this.stuckFor + dt : reversing || speed > 2 ? 0 : this.stuckFor;
    if (this.show.backOut && (reversing || car.vx > 2)) this.show.backOut = null;
    else if (!this.show.backOut && this.stuckFor >= STUCK_SECONDS && this.stuckFor - dt < STUCK_SECONDS) this.offer('backOut');

    // Spun: the wrong way round for a moment.
    this.spunFor = facingBack ? this.spunFor + dt : 0;
    if (this.spunFor >= SPUN_SECONDS && progress < RESTART_BEFORE) this.offerRestart();
    if (this.stuckFor >= STUCK_LONG_SECONDS) this.offerRestart();

    if (this.show.restart) {
      this.restartLeft -= dt;
      if (this.restartLeft <= 0) this.show.restart = null;
    }
    return before !== `${this.show.backOut}|${this.show.restart}`;
  }

  /** A wall hit (m/s into it). */
  hit(speed: number, progress: number): void {
    if (speed >= HARD_HIT && progress < RESTART_BEFORE) this.offerRestart();
  }

  /** The lap went invalid (all four wheels off). */
  invalid(progress: number): void {
    if (progress < RESTART_BEFORE) this.offerRestart();
  }

  /** Restart pressed: told, it's counted, and they know it from now on; on their own, likewise. */
  restartPressed(): void {
    if (this.show.restart) {
      if (this.teaching) return;
      this.note('restartAfterHint');
      this.memory.write(KEY.knows.restart, '1');
    } else if (!this.knows('restart')) {
      this.memory.write(KEY.knows.restart, '1');
      this.note('knewRestart');
    }
  }

  private offerRestart(): void {
    if (this.restartOffered || this.show.restart) return;
    this.restartOffered = true;
    if (this.offer('restart')) this.restartLeft = RESTART_SHOW;
  }

  /** Put a hint up: words the first few times, a glow after; never once they know (the lesson: always words). */
  private offer(h: RescueHint): boolean {
    if (this.teaching) {
      this.show[h] = 'words';
      return true;
    }
    if (this.knows(h)) return false;
    const shown = Number(this.memory.read(KEY.shown[h]) ?? 0) || 0;
    this.show[h] = shown < WORDS_TIMES ? 'words' : 'glow';
    this.memory.write(KEY.shown[h], String(shown + 1));
    this.note(h === 'backOut' ? 'backOutHint' : 'restartHint');
    return true;
  }

  private knows(h: RescueHint): boolean {
    return this.memory.read(KEY.knows[h]) === '1';
  }
}
